import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  healthFormsApi,
  QuestionListItem,
  QuestionListParams,
  QuestionnaireListItem,
  QuestionGroupListItem,
  SearchParams,
  UsageItem,
  PublishedQuestionnaireListItem,
  PublishedQuestionnaireDetail,
  PublishCheckConflict,
  PublishedQuestionnaireHistoryItem,
  PatientPreviewResponse,
  QuestionPreviewResponse,
  QuestionGroupPreviewResponse,
} from "./api";
import type { PreviewAttrs } from "./api";
import { Question } from "./types/common";
import { Questionnaire, QuestionGroup, ExpandedQuestionnaire, ExpandedQuestionGroup } from "./types/persisted";

export const queryKeys = {
  questionsBase: ["healthForms", "questions"] as const,
  questions: (params?: QuestionListParams) => ["healthForms", "questions", "list", params] as const,
  question: (id: number) => ["healthForms", "questions", id] as const,
  questionnaires: (params?: SearchParams) => ["healthForms", "questionnaires", "list", params] as const,
  questionnairesBase: ["healthForms", "questionnaires"] as const,
  questionnaire: (id: number) => ["healthForms", "questionnaires", id] as const,
  questionnaireByName: (name: string) => ["healthForms", "questionnaires", "by-name", name] as const,
  questionGroups: (params?: SearchParams) => ["healthForms", "questionGroups", "list", params] as const,
  questionGroupsBase: ["healthForms", "questionGroups"] as const,
  questionGroup: (id: number) => ["healthForms", "questionGroups", id] as const,
  questionUsage: (id: number) => ["healthForms", "questions", id, "usage"] as const,
  questionGroupUsage: (id: number) => ["healthForms", "questionGroups", id, "usage"] as const,
  publishedQuestionnaires: (params?: SearchParams) =>
    ["healthForms", "publishedQuestionnaires", "list", params] as const,
  publishedQuestionnairesBase: ["healthForms", "publishedQuestionnaires"] as const,
  publishedQuestionnaire: (id: number) => ["healthForms", "publishedQuestionnaires", id] as const,
  questionnairePreview: (id: number, attrs: PreviewAttrs) =>
    ["healthForms", "questionnaires", id, "preview", attrs] as const,
  questionPreview: (id: number, attrs: PreviewAttrs) => ["healthForms", "questions", id, "preview", attrs] as const,
  questionGroupPreview: (id: number, attrs: PreviewAttrs) =>
    ["healthForms", "questionGroups", id, "preview", attrs] as const,
  publishedQuestionnairePreview: (id: number, attrs: PreviewAttrs) =>
    ["healthForms", "publishedQuestionnaires", id, "preview", attrs] as const,
  questionnaireDiffResolved: (id: number, locale: string) =>
    ["healthForms", "questionnaires", id, "diffResolved", locale] as const,
  publishedQuestionnaireDiffResolved: (id: number, locale: string) =>
    ["healthForms", "publishedQuestionnaires", id, "diffResolved", locale] as const,
  publishedQuestionnaireHistory: (name: string) => ["healthForms", "publishedQuestionnaires", "history", name] as const,
  publishCheck: (id: number) => ["healthForms", "publishCheck", id] as const,
};

// The questionnaire list is the only one carrying derived state (has_unpublished_changes),
// and it rolls up from the questions and groups it contains. So every content mutation
// refreshes its own list plus the questionnaire list.
const useHealthFormMutation = <TData, TVars>(
  ownBase: readonly unknown[],
  mutationFn: (vars: TVars) => Promise<TData>
) => {
  const queryClient = useQueryClient();
  return useMutation(mutationFn, {
    onSuccess: () => {
      queryClient.invalidateQueries(ownBase);
      queryClient.invalidateQueries(queryKeys.questionnairesBase);
    },
  });
};

export const useQuestions = (params?: QuestionListParams) =>
  useQuery<QuestionListItem[]>(queryKeys.questions(params), () => healthFormsApi.questions.list(params));

export const useQuestion = (id: number | null) =>
  useQuery<Question>(queryKeys.question(id ?? 0), () => healthFormsApi.questions.get(id!), {
    enabled: !!id,
  });

export const useUpdateQuestion = () =>
  useHealthFormMutation(queryKeys.questionsBase, ({ id, question }: { id: number; question: Question }) =>
    healthFormsApi.questions.update(id, question)
  );

export const useDeleteQuestion = () =>
  useHealthFormMutation(queryKeys.questionsBase, (id: number) => healthFormsApi.questions.delete(id));

export const useQuestionnaires = (params?: SearchParams) =>
  useQuery<QuestionnaireListItem[]>(queryKeys.questionnaires(params), () => healthFormsApi.questionnaires.list(params));

export const useQuestionnaire = (id: number | null) =>
  useQuery<ExpandedQuestionnaire>(queryKeys.questionnaire(id ?? 0), () => healthFormsApi.questionnaires.get(id!), {
    enabled: !!id,
  });

export const useQuestionnaireByName = (name: string | null) =>
  useQuery<QuestionnaireListItem | null>(
    queryKeys.questionnaireByName(name ?? ""),
    () => healthFormsApi.questionnaires.getByName(name!),
    { enabled: !!name }
  );

export const useUpdateQuestionnaire = () =>
  useHealthFormMutation(
    queryKeys.questionnairesBase,
    ({ id, questionnaire }: { id: number; questionnaire: Questionnaire }) =>
      healthFormsApi.questionnaires.update(id, questionnaire)
  );

export const useDeleteQuestionnaire = () =>
  useHealthFormMutation(queryKeys.questionnairesBase, (id: number) => healthFormsApi.questionnaires.delete(id));

export const useQuestionGroups = (params?: SearchParams) =>
  useQuery<QuestionGroupListItem[]>(queryKeys.questionGroups(params), () => healthFormsApi.questionGroups.list(params));

export const useQuestionGroup = (id: number | null) =>
  useQuery<ExpandedQuestionGroup>(queryKeys.questionGroup(id ?? 0), () => healthFormsApi.questionGroups.get(id!), {
    enabled: !!id,
  });

export const useUpdateQuestionGroup = () =>
  useHealthFormMutation(queryKeys.questionGroupsBase, ({ id, group }: { id: number; group: QuestionGroup }) =>
    healthFormsApi.questionGroups.update(id, group)
  );

export const useDeleteQuestionGroup = () =>
  useHealthFormMutation(queryKeys.questionGroupsBase, (id: number) => healthFormsApi.questionGroups.delete(id));

export const useCreateQuestion = () =>
  useHealthFormMutation(queryKeys.questionsBase, () => healthFormsApi.questions.create());

export const useCreateQuestionnaire = () =>
  useHealthFormMutation(queryKeys.questionnairesBase, (input?: { name?: string; pages?: Questionnaire["pages"] }) =>
    healthFormsApi.questionnaires.create(input)
  );

export const useCreateQuestionGroup = () =>
  useHealthFormMutation(
    queryKeys.questionGroupsBase,
    (input?: Omit<QuestionGroup, "_type" | "id" | "updated_at" | "edited_by">) =>
      healthFormsApi.questionGroups.create(input)
  );

export const useQuestionUsage = (id: number) =>
  useQuery<UsageItem[]>(queryKeys.questionUsage(id), () => healthFormsApi.questions.usage(id));

export const useQuestionGroupUsage = (id: number) =>
  useQuery<UsageItem[]>(queryKeys.questionGroupUsage(id), () => healthFormsApi.questionGroups.usage(id));

export const usePublishedQuestionnaires = (params?: SearchParams) =>
  useQuery<PublishedQuestionnaireListItem[]>(queryKeys.publishedQuestionnaires(params), () =>
    healthFormsApi.publishedQuestionnaires.list(params)
  );

export const usePublishedQuestionnaire = (id: number | null) =>
  useQuery<PublishedQuestionnaireDetail>(
    queryKeys.publishedQuestionnaire(id ?? 0),
    () => healthFormsApi.publishedQuestionnaires.get(id!),
    { enabled: !!id }
  );

export const useQuestionnairePreview = (id: number | null, attrs: PreviewAttrs) =>
  useQuery<PatientPreviewResponse>(
    queryKeys.questionnairePreview(id ?? 0, attrs),
    () => healthFormsApi.questionnaires.preview(id!, attrs),
    { enabled: !!id }
  );

export const useQuestionPreview = (id: number | null, attrs: PreviewAttrs) =>
  useQuery<QuestionPreviewResponse>(
    queryKeys.questionPreview(id ?? 0, attrs),
    () => healthFormsApi.questions.preview(id!, attrs),
    { enabled: !!id }
  );

export const useQuestionGroupPreview = (id: number | null, attrs: PreviewAttrs) =>
  useQuery<QuestionGroupPreviewResponse>(
    queryKeys.questionGroupPreview(id ?? 0, attrs),
    () => healthFormsApi.questionGroups.preview(id!, attrs),
    { enabled: !!id }
  );

export const usePublishedQuestionnairePreview = (id: number | null, attrs: PreviewAttrs) =>
  useQuery<PatientPreviewResponse>(
    queryKeys.publishedQuestionnairePreview(id ?? 0, attrs),
    () => healthFormsApi.publishedQuestionnaires.preview(id!, attrs),
    { enabled: !!id }
  );

export const usePublishCheck = (id: number | null) =>
  useQuery<PublishCheckConflict | null>(
    queryKeys.publishCheck(id ?? 0),
    () => healthFormsApi.questionnaires.publishCheck(id!),
    { enabled: !!id }
  );

export const usePublishedQuestionnaireHistory = (name: string | null) =>
  useQuery<PublishedQuestionnaireHistoryItem[]>(
    queryKeys.publishedQuestionnaireHistory(name ?? ""),
    () => healthFormsApi.publishedQuestionnaires.history(name!),
    { enabled: !!name }
  );

export const usePublishQuestionnaire = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => healthFormsApi.questionnaires.publish(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.publishedQuestionnairesBase);
      queryClient.invalidateQueries(queryKeys.questionnairesBase);
    },
  });
};

export const useInactivatePublishedQuestionnaire = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => healthFormsApi.publishedQuestionnaires.inactivate(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.publishedQuestionnairesBase);
      queryClient.invalidateQueries(queryKeys.questionnairesBase);
    },
  });
};
